// Importa classes do Lit para criar Web Components
import { LitElement, html } from 'lit'
// Importa mixin de salvamento com loading, erro inline e integração com cache service
import { ModalSaveMixin } from '../core/mixins/modal-save.mixin.js'
// Importa utilitário de cálculo de status local para exibição no modo edição
import { calcularStatusLocal } from '../core/utils/status-transacao.util.js'
// Importa modals relacionados
import './modal-categoria.js'
import './modal-conta.js'
import './modal-responsavel.js'
import './modal-tag.js'
// Importa cache services
import { contaCacheService } from '../core/services/conta-cache.service.js'
import { responsavelCacheService } from '../core/services/responsavel-cache.service.js'
import { categoriaCacheService } from '../core/services/categoria-cache.service.js'
import { tagCacheService } from '../core/services/tag-cache.service.js'
import { transacaoCacheService } from '../core/services/transacao-cache.service.js'


/**
 * Modal complexo para criar e editar transações financeiras
 * Suporta receitas, despesas, parcelamento, recorrência e divisão entre responsáveis
 * Integra com todos os modals relacionados (conta, categoria, responsável, tag)
 * Inclui validações, formatação de moeda e cálculos automáticos
 *
 * Aplica ModalSaveMixin para integração com History API (popstate),
 * botão voltar iOS, evento padronizado modal-closed, loading no botão Salvar,
 * erro inline e chamada direta ao Cache Service.
 */
class ModalTransacao extends ModalSaveMixin(LitElement) {
  static properties = {
    open: { type: Boolean, reflect: true },
    showSplits: { type: Boolean },
    splitMode: { type: String },
    splits: { type: Array },
    isInstallment: { type: Boolean },
    isRecurring: { type: Boolean },
    hasRecurringEnd: { type: Boolean },
    recurringEndType: { type: String },
    tags: { type: Array },
    selectedType: { type: String },
    isEditMode: { type: Boolean },
    transactionData: { type: Object },
    responsiblePerson: { type: String },
    callerModal: { type: String },
    loading: { type: Boolean },
    contaOrigemId: { type: String },
    contaDestinoId: { type: String },
    modalidadePagamento: { type: String },
    dataVencimento: { type: String },
    dataPagamento: { type: String },
    _showTagDropdown: { type: Boolean, state: true }
  }

  /**
   * Nome da entidade para identificação nos eventos e no data-cy de erro.
   * Usado pelo ModalSaveMixin para disparar cache-operation-started/finished
   * e renderizar erro inline com data-cy="erro-salvar-transacao".
   * @type {string}
   */
  _entidade = 'transacao'

  createRenderRoot() {
    return this // manter estilos do Bulma
  }

  constructor() {
    super()
    this.open = false
    this.showSplits = false
    this.splitMode = 'percentage'
    this.splits = []
    this.isInstallment = false
    this.isRecurring = false
    this.hasRecurringEnd = false
    this.recurringEndType = 'date'
    this.tags = []
    this.selectedType = 'despesa'
    this.isEditMode = false
    this.transactionData = null
    this.responsiblePerson = null
    this.callerModal = null
    this.loading = false
    this.contaOrigemId = ''
    this.contaDestinoId = ''
    this.modalidadePagamento = 'a_vista'
    this.dataVencimento = null
    this.dataPagamento = null
    this.contas = []
    this.categorias = []
    this.responsaveis = []
    this.tagsDisponiveis = []
    this.originalTheme = null
    this.currentUserEmail = ''
    this.selectedContaId = null
    this._showTagDropdown = false


    
    // Inicializar serviços com cache
    this.contaCacheService = contaCacheService
    this.categoriaCacheService = categoriaCacheService
    this.responsavelCacheService = responsavelCacheService
    this.tagCacheService = tagCacheService
    this.transacaoService = transacaoCacheService
    
    this._getCurrentUserEmail()
    this._loadData()
    this._setupFieldErrorClearListeners()
    
    // Escutar atualizações
    // Recarrega contas quando o cache é atualizado (criação/edição/exclusão)
    // Detecta nova conta e a seleciona automaticamente no select
    window.addEventListener('contas-cache-updated', async () => {
      const contasAntes = new Set((this.contas || []).map(c => c.id))
      this.contas = await this.contaCacheService.listar()
      this.requestUpdate()

      // Detecta conta nova e seleciona automaticamente
      const novaConta = this.contas.find(c => !contasAntes.has(c.id))
      if (novaConta) {
        await this.updateComplete
        const select = this.renderRoot.querySelector('[data-cy="select-conta"]')
        if (select) {
          select.value = novaConta.id
        }
      }
    })
    
    // Recarrega categorias quando o cache é atualizado (criação/edição/exclusão)
    // Detecta nova categoria e a seleciona automaticamente no select
    window.addEventListener('categorias-cache-updated', async () => {
      const categoriasAntes = new Set((this.categorias || []).map(c => c.id))
      this.categorias = await this.categoriaCacheService.listar()
      this.requestUpdate()

      // Detecta categoria nova (não existia antes) e seleciona automaticamente
      const novaCat = this.categorias.find(c => !categoriasAntes.has(c.id))
      if (novaCat) {
        await this.updateComplete
        const select = this.renderRoot.querySelector('[data-cy="select-categoria"]')
        if (select) {
          select.value = novaCat.id
        }
      }
    })
    
    // Responsáveis são atualizados via @modal-closed no <modal-responsavel>
    // Não usar evento window pois o timing pode causar race condition com a seleção
    
    // Tags são atualizadas via @modal-closed no <modal-tag>
    // Não usar evento window pois o timing pode causar race condition com a adição à transação
  }

  /**
   * Fecha o modal ao clicar no backdrop (área escurecida)
   * Delega para this.close() que já gerencia o estado do modal
   */
  _handleBackdropClick() {
    this.close()
  }

  /**
   * Impede propagação do clique no card para o backdrop
   * Evita que cliques dentro do modal disparem o fechamento
   * @param {Event} e - Evento de clique
   */
  _stopPropagation(e) {
    e.stopPropagation()
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Salvar/Atualizar
   * Replica o padrão do floating-button com transição suave de cor
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onSaveHover(e) {
    e.target.style.backgroundColor = 'var(--hover-primary, #7B3EF0)'
  }

  /**
   * Restaura cor original do botão Salvar/Atualizar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onSaveLeave(e) {
    e.target.style.backgroundColor = ''
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Cancelar
   * Usa background secundário para destacar o botão neutro
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onCancelHover(e) {
    e.target.style.backgroundColor = 'var(--glass-bg, rgba(117, 133, 255, 0.22))'
  }

  /**
   * Restaura cor original do botão Cancelar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onCancelLeave(e) {
    e.target.style.backgroundColor = ''
  }

  /**
   * Fecha o modal de transação com lógica específica.
   *
   * Delega para super.close() do ModalSaveMixin que implementa guard contra
   * _saving = true, reset de estado e integração com History API.
   * Restaura o tema original do documento (se alterado durante edição)
   * e dispara evento return-to-caller quando o modal foi aberto por outro modal.
   *
   * @param {object} [options={}] - Opções de fechamento repassadas ao mixin base
   * @param {boolean} [options.fromPopstate=false] - Indica se veio do popstate
   */
  close(options = {}) {
    // Guard do ModalSaveMixin: impede fechamento durante salvamento
    if (this._saving) return

    // Restaurar tema original
    if (this.originalTheme) {
      document.documentElement.setAttribute('data-theme', this.originalTheme)
      this.originalTheme = null
    }
    
    if (this.callerModal) {
      this.dispatchEvent(new CustomEvent('return-to-caller', {
        detail: { callerModal: this.callerModal, transaction: this.transactionData },
        bubbles: true
      }))
      this.callerModal = null
    }

    super.close(options)
  }

  async show(transactionData = null, callerModal = null) {
    this.isEditMode = !!transactionData
    this.transactionData = transactionData
    this.callerModal = callerModal
    
    await this._loadData()
    
    if (this.isEditMode && transactionData) {
      this._loadTransactionData(transactionData)
    } else {
      await this._resetForm()
    }
    
    // Armazenar tema atual sem forçar mudança
    this.originalTheme = document.documentElement.getAttribute('data-theme') || 'light'
    
    this.open = true
    
    // Scroll para o topo
    setTimeout(() => {
      const modal = this.renderRoot.querySelector('.card')
      if (modal) modal.scrollTop = 0
    }, 0)
  }

  _loadTransactionData(data) {
    this.selectedType = data.tipo || (data.valor >= 0 ? 'receita' : 'despesa')
    this.tags = [...(data.tagNomes || [])]
    this.responsiblePerson = data.responsavelId || this.responsiblePerson

    // Carregar campos de modalidade e datas de efetivação
    // Compatibilidade retroativa: inferir modalidade a partir de flags legadas
    if (data.modalidadePagamento) {
      this.modalidadePagamento = data.modalidadePagamento
    } else if (data.recorrente) {
      this.modalidadePagamento = 'recorrente'
    } else if (data.parcelado) {
      this.modalidadePagamento = 'parcelado'
    } else {
      this.modalidadePagamento = 'a_vista'
    }

    this.dataVencimento = data.dataVencimento || null
    this.dataPagamento = data.dataPagamento || null

    // Sincronizar isInstallment/isRecurring com a modalidade carregada
    this.isInstallment = (this.modalidadePagamento === 'parcelado')
    this.isRecurring = (this.modalidadePagamento === 'recorrente')
    
    // Carregar dados de transferência se existirem
    if (data.tipo === 'transferencia' && data.infoTransferencia) {
      this.contaOrigemId = data.infoTransferencia.contaOrigemId || ''
      this.contaDestinoId = data.infoTransferencia.contaDestinoId || ''
    } else {
      this.contaOrigemId = ''
      this.contaDestinoId = ''
    }
    
    // Carregar divisões se existirem
    if (data.compartilhado && data.divisoes && data.divisoes.length > 0) {
      this.showSplits = true
      this.splits = data.divisoes.map(divisao => ({
        person: divisao.responsavelId,
        percentage: divisao.percentual || 0,
        value: divisao.valor || 0
      }))
    } else {
      this.showSplits = false
      this.splits = []
    }
    
    setTimeout(() => {
      const inputs = this.renderRoot.querySelectorAll('input, select')
      inputs.forEach(input => {
        switch(input.dataset.cy) {
          case 'input-valor':
            const formattedValue = Math.abs(data.valor).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL'
            })
            input.value = formattedValue
            break
          case 'input-descricao':
            input.value = data.descricao
            break
          case 'select-categoria':
            input.value = data.categoriaId
            break
          case 'select-conta':
            input.value = data.contaId
            break
          case 'input-data':
            if (data.data) {
              const [day, month, year] = data.data.split('/')
              input.value = `${year}-${month}-${day}`
            }
            break
          case 'checkbox-dividir-despesa':
            input.checked = this.showSplits
            break
          case 'select-responsavel':
            input.value = this.responsiblePerson
            break
          case 'select-conta-origem':
            // Preencher select de conta origem ao editar transferência
            input.value = this.contaOrigemId
            break
          case 'select-conta-destino':
            // Preencher select de conta destino ao editar transferência
            input.value = this.contaDestinoId
            break
        }
      })
    }, 0)
  }



  async _resetForm() {
    this.selectedType = 'despesa'
    this.tags = []
    this.isRecurring = false
    this.isInstallment = false
    this.showSplits = false
    this.hasRecurringEnd = false
    this.splits = []
    this.modalidadePagamento = 'a_vista'
    this.dataVencimento = null
    this.dataPagamento = null
    
    // Definir responsável padrão como responsável principal (usuário logado)
    if (this.responsaveis.length > 0) {
      const currentUserEmail = this.currentUserEmail || localStorage.getItem('userEmail') || ''
      const responsavelPrincipal = this.responsaveis.find(r => r.email === currentUserEmail)
      this.responsiblePerson = responsavelPrincipal ? responsavelPrincipal.id : this.responsaveis[0].id
      
      // Forçar atualização do select no DOM
      this.requestUpdate()
      setTimeout(() => {
        const responsavelSelect = this.renderRoot.querySelector('[data-cy="select-responsavel"]')
        if (responsavelSelect) {
          responsavelSelect.value = this.responsiblePerson
        }
      }, 100)
    } else {
      this.responsiblePerson = null
    }
    
    // Limpar conta selecionada
    this.selectedContaId = null
    
    // Limpar campos de transferência
    this.contaOrigemId = ''
    this.contaDestinoId = ''
    
    // Limpar campos do DOM
    setTimeout(() => {
      const valorInput = this.renderRoot.querySelector('[data-cy="input-valor"]')
      const descricaoInput = this.renderRoot.querySelector('[data-cy="input-descricao"]')
      const categoriaSelect = this.renderRoot.querySelector('[data-cy="select-categoria"]')
      const contaSelect = this.renderRoot.querySelector('[data-cy="select-conta"]')
      
      if (valorInput) valorInput.value = ''
      if (descricaoInput) descricaoInput.value = ''
      if (categoriaSelect) categoriaSelect.value = ''
      if (contaSelect) contaSelect.value = this.selectedContaId || ''
    }, 0)
    
    // Não limpar callerModal aqui
  }

  /**
   * Seleciona o tipo de transação (despesa, receita ou transferência)
   * Limpa campos que não se aplicam ao tipo selecionado
   * @param {string} type - Tipo selecionado: 'despesa', 'receita' ou 'transferencia'
   */
  _selectType(type) {
    this.selectedType = type
    // Limpar seleção de categoria ao mudar tipo
    const select = this.renderRoot.querySelector('[data-cy="select-categoria"]')
    if (select) select.value = ''
    
    // Limpar campos de transferência ao mudar para outro tipo
    if (type !== 'transferencia') {
      this.contaOrigemId = ''
      this.contaDestinoId = ''
    }
  }

  /**
   * Retorna apenas contas ativas do usuário para popular selects de transferência
   * Filtra contas com campo ativo !== false (contas sem campo ativo são consideradas ativas)
   * @returns {Array} Lista de contas ativas
   */
  _getContasAtivas() {
    return this.contas.filter(conta => conta.ativo !== false)
  }

  _toggleSplits(e) {
    this.showSplits = e.target.checked
    
    if (this.showSplits) {
      // Adicionar primeira divisão com responsável selecionado
      const totalValue = this._getTotalValue()
      this.splits = [{ person: this.responsiblePerson, percentage: 100, value: totalValue || 0, manuallyEdited: false }]
    } else {
      this.splits = []
    }
  }

  _setSplitMode(mode) {
    // Ao trocar de modo, recalcular valores mantendo a lógica de centavos
    if (mode !== this.splitMode) {
      this._recalcularSplits()
    }
    this.splitMode = mode
  }

  /**
   * Recalcula os valores e percentuais dos splits.
   * Começa sempre pelo valor em centavos, depois calcula o percentual.
   * Diferença de arredondamento vai para o primeiro split não editado manualmente.
   * Splits editados manualmente mantêm seu valor.
   * @private
   */
  _recalcularSplits () {
    const total = this._getTotalValue()
    if (total <= 0 || this.splits.length === 0) return

    const splitsEditados = this.splits.filter(s => s.manuallyEdited)
    const splitsAuto = this.splits.filter(s => !s.manuallyEdited)

    if (splitsAuto.length === 0) return // Todos editados manualmente, nada a recalcular

    // Valor já consumido pelos editados manualmente
    const valorEditado = splitsEditados.reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0)
    const valorRestante = Math.max(0, total - valorEditado)

    // Distribuir igualmente entre os automáticos (em centavos para precisão)
    const centavosRestante = Math.round(valorRestante * 100)
    const centavosPorSplit = Math.floor(centavosRestante / splitsAuto.length)
    let centavosSobra = centavosRestante - (centavosPorSplit * splitsAuto.length)

    // Atribuir valores — sobra de centavos vai para o primeiro automático
    const newSplits = this.splits.map(split => {
      if (split.manuallyEdited) {
        // Manter valor manual, recalcular apenas percentual
        const valor = parseFloat(split.value) || 0
        return { ...split, percentage: parseFloat(((valor / total) * 100).toFixed(2)) }
      }

      let centavos = centavosPorSplit
      if (centavosSobra > 0) {
        centavos += 1
        centavosSobra--
      }
      const valor = centavos / 100
      const percentage = parseFloat(((valor / total) * 100).toFixed(2))
      return { ...split, value: valor.toFixed(2), percentage }
    })

    this.splits = newSplits
  }

  /**
   * Adiciona novo responsável à divisão de despesas.
   * Seleciona o próximo responsável disponível (não usado em outros splits).
   * Recalcula automaticamente os valores entre os splits não editados.
   */
  _addSplit() {
    // Encontrar o próximo responsável disponível (não usado em splits existentes)
    const usedPersonIds = new Set(this.splits.map(s => s.person))
    const nextResponsavel = this.responsaveis.find(r => !usedPersonIds.has(r.id))
    // Se não há responsável disponível, deixar vazio (null)
    const defaultResponsavel = nextResponsavel ? nextResponsavel.id : null
    
    // Adicionar novo split sem valor (será recalculado)
    this.splits = [...this.splits, { 
      person: defaultResponsavel, 
      percentage: 0, 
      value: 0,
      manuallyEdited: false
    }]

    // Recalcular todos os não-editados (inclui o novo)
    this._recalcularSplits()
  }

  _removeSplit(index) {
    this.splits = this.splits.filter((_, i) => i !== index)
    // Recalcular após remoção
    this._recalcularSplits()
  }

  _updateSplit(index, field, value) {
    const newSplits = [...this.splits]
    newSplits[index] = { ...newSplits[index], [field]: value }
    this.splits = newSplits
  }

  /**
   * Handler de edição manual de percentual em um split.
   * Aplica máscara com vírgula e símbolo %.
   * Marca como editado manualmente e recalcula o restante.
   * @param {Event} e - Evento do input
   * @param {number} index - Índice do split editado
   * @private
   */
  _onSplitPercentageInput (e, index) {
    // Extrair apenas dígitos do valor digitado
    let rawValue = e.target.value.replace(/[^\d]/g, '')
    if (rawValue === '') {
      e.target.value = ''
      const newSplits = [...this.splits]
      newSplits[index] = { ...newSplits[index], percentage: 0, value: '0.00', manuallyEdited: true }
      this.splits = newSplits
      this._recalcularSplits()
      return
    }

    // Tratar como centésimos de percentual (2 casas decimais)
    const numericValue = parseInt(rawValue) / 100
    const percentage = Math.min(numericValue, 100)

    // Formatar com vírgula e %
    e.target.value = percentage.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%'

    const total = this._getTotalValue()
    const valorCalculado = total > 0 ? ((percentage / 100) * total).toFixed(2) : '0.00'

    const newSplits = [...this.splits]
    newSplits[index] = { ...newSplits[index], percentage, value: valorCalculado, manuallyEdited: true }
    this.splits = newSplits

    // Recalcular os não-editados
    this._recalcularSplits()
  }

  /**
   * Handler de edição manual de valor em um split.
   * Marca como editado manualmente e recalcula o restante.
   * @param {Event} e - Evento do input
   * @param {number} index - Índice do split editado
   * @private
   */
  _onSplitValueInput (e, index) {
    let rawValue = e.target.value.replace(/\D/g, '')
    if (rawValue === '') {
      e.target.value = ''
      const newSplits = [...this.splits]
      newSplits[index] = { ...newSplits[index], value: '0.00', manuallyEdited: true }
      this.splits = newSplits
      this._recalcularSplits()
      return
    }

    const numericValue = parseInt(rawValue) / 100
    e.target.value = numericValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })

    const total = this._getTotalValue()
    const percentage = total > 0 ? parseFloat(((numericValue / total) * 100).toFixed(2)) : 0

    const newSplits = [...this.splits]
    newSplits[index] = { ...newSplits[index], value: numericValue.toFixed(2), percentage, manuallyEdited: true }
    this.splits = newSplits

    // Recalcular os não-editados
    this._recalcularSplits()
  }

  /**
   * Calcula o total de valores dos splits para validação visual.
   * @returns {number} Soma dos valores de todos os splits
   * @private
   */
  _getTotalSplitValue () {
    return this.splits.reduce((sum, split) => sum + (parseFloat(split.value) || 0), 0)
  }

  _getTotalPercentage() {
    return parseFloat(this.splits.reduce((sum, split) => sum + (parseFloat(split.percentage) || 0), 0).toFixed(2))
  }

  _toggleInstallment(e) {
    this.isInstallment = e.target.checked
    if (this.isInstallment) {
      this.isRecurring = false
    }
  }

  _toggleRecurring(e) {
    this.isRecurring = e.target.checked
    if (this.isRecurring) {
      this.isInstallment = false
    }
  }

  /**
   * Handler de mudança de modalidade de pagamento.
   * Aplica regras de transição entre modalidades:
   * - "a_prazo": limpa dataPagamento (transação pendente)
   * - "a_vista": preenche dataPagamento com data de lançamento (transação efetivada)
   * - "parcelado": ativa painel de parcelamento, desativa recorrência
   * - "recorrente": ativa painel de recorrência, desativa parcelamento
   *
   * @param {string} novaModalidade - Nova modalidade selecionada: 'a_vista', 'a_prazo', 'parcelado' ou 'recorrente'
   */
  _onModalidadeChange (novaModalidade) {
    this.modalidadePagamento = novaModalidade

    // Regra: ao mudar para "a_prazo", limpar dataPagamento e preencher dataVencimento
    if (novaModalidade === 'a_prazo') {
      this.dataPagamento = null
      if (!this.dataVencimento) {
        this.dataVencimento = this._calcularDataVencimentoDefault()
      }
    }

    // Regra: ao mudar para "a_vista", preencher dataPagamento com data de lançamento
    if (novaModalidade === 'a_vista') {
      this.dataPagamento = this._getDataNormalizada()
      this.dataVencimento = null
    }

    // Parcelar: ativa painel de parcelamento, desativa recorrência
    if (novaModalidade === 'parcelado') {
      this.isInstallment = true
      this.isRecurring = false
    } else if (novaModalidade === 'recorrente') {
      // Recorrente: ativa painel de recorrência, desativa parcelamento
      this.isRecurring = true
      this.isInstallment = false
    } else {
      // À vista ou à prazo: desativa ambos os painéis
      this.isInstallment = false
      this.isRecurring = false
    }
  }

  /**
   * Retorna a data do campo input-data normalizada em formato YYYY-MM-DD.
   * Usado para preencher dataPagamento ao selecionar modalidade "À vista".
   *
   * @returns {string} Data no formato YYYY-MM-DD
   */
  _getDataNormalizada () {
    const dataInput = this.renderRoot.querySelector('[data-cy="input-data"]')
    return dataInput?.value || new Date().toISOString().split('T')[0]
  }

  /**
   * Calcula a data de vencimento padrão: mesmo dia do mês seguinte.
   * Ajusta para meses curtos (ex: 31/01 → 28/02 em anos não-bissextos).
   *
   * @returns {string} Data de vencimento padrão no formato YYYY-MM-DD
   */
  _calcularDataVencimentoDefault () {
    const dataInput = this.renderRoot.querySelector('[data-cy="input-data"]')
    const dataBase = dataInput?.value || new Date().toISOString().split('T')[0]

    const [ano, mes, dia] = dataBase.split('-').map(Number)

    // Calcular próximo mês
    let novoMes = mes + 1
    let novoAno = ano
    if (novoMes > 12) {
      novoMes = 1
      novoAno++
    }

    // Ajustar para meses curtos (último dia do mês se o dia original não existe)
    const ultimoDiaMes = new Date(novoAno, novoMes, 0).getDate()
    const novoDia = Math.min(dia, ultimoDiaMes)

    return `${novoAno}-${String(novoMes).padStart(2, '0')}-${String(novoDia).padStart(2, '0')}`
  }

  /**
   * Renderiza o toggle de modalidade de pagamento.
   * Usa o mesmo estilo visual do toggle de divisão (% / R$) existente no modal.
   * Para transferências: apenas "À vista" e "Agendada"
   * Para receitas/despesas: "À vista", "À prazo", "Parcelar", "Recorrente"
   *
   * @returns {import('lit').TemplateResult} Template do toggle de modalidade
   */
  _renderToggleModalidade () {
    const isTransferencia = this.selectedType === 'transferencia'

    const opcoes = isTransferencia
      ? [
          { value: 'a_vista', label: 'À vista' },
          { value: 'a_prazo', label: 'Agendada' }
        ]
      : [
          { value: 'a_vista', label: 'À vista' },
          { value: 'a_prazo', label: 'À prazo' },
          { value: 'parcelado', label: 'Parcelar' },
          { value: 'recorrente', label: 'Recorrente' }
        ]

    return html`
      <div class="field" data-cy="toggle-modalidade">
        <label class="label is-size-7 has-text-weight-medium">Modalidade</label>
        <div class="buttons has-addons">
          ${opcoes.map(op => html`
            <button
              type="button"
              class="button is-small ${this.modalidadePagamento === op.value ? 'is-primary is-selected' : ''}"
              data-cy="btn-modalidade-${op.value}"
              @click=${() => this._onModalidadeChange(op.value)}
            >${op.label}</button>
          `)}
        </div>
      </div>
    `
  }

  /**
   * Renderiza os campos condicionais de data de vencimento e data de pagamento.
   * Visibilidade controlada pela modalidade selecionada:
   * - "À vista": oculta ambos os campos
   * - "À prazo" / "Agendada": exibe dataVencimento e dataPagamento
   *
   * Labels variam conforme tipo da transação:
   * - Transferências: "Data de agendamento" para dataVencimento
   * - Receitas/Despesas: "Data de vencimento" para dataVencimento
   *
   * @returns {import('lit').TemplateResult|string} Template dos campos ou vazio
   */
  _renderCamposDatas () {
    // Modalidade "a_vista" oculta ambos os campos
    if (this.modalidadePagamento !== 'a_prazo') return ''

    const isTransferencia = this.selectedType === 'transferencia'
    const labelVencimento = isTransferencia ? 'Data de agendamento' : 'Data de vencimento'

    return html`
      <div class="field mb-4">
        <label class="label is-size-7 has-text-weight-medium">${labelVencimento}</label>
        <div class="control has-icons-left">
          <input
            class="input"
            type="date"
            data-cy="input-data-vencimento"
            .value="${this.dataVencimento || ''}"
            @change="${(e) => { this.dataVencimento = e.target.value }}"
            @input="${(e) => { this.dataVencimento = e.target.value }}"
          />
          <span class="icon is-left">
            <i class="fas fa-calendar"></i>
          </span>
        </div>
      </div>

      <div class="field mb-4">
        <label class="label is-size-7 has-text-weight-medium">Data de pagamento</label>
        <div class="control has-icons-left">
          <input
            class="input"
            type="date"
            data-cy="input-data-pagamento"
            .value="${this.dataPagamento || ''}"
            @change="${(e) => { this.dataPagamento = e.target.value || null }}"
            @input="${(e) => { this.dataPagamento = e.target.value || null }}"
          />
          <span class="icon is-left">
            <i class="fas fa-calendar"></i>
          </span>
        </div>
      </div>
    `
  }

  /**
   * Renderiza o label de status da transação (apenas em modo edição).
   * Utiliza calcularStatusLocal para derivar o status a partir dos campos atuais.
   * Exibe badge colorido conforme status:
   * - is-success: paga, recebida, efetivada
   * - is-warning: a_vencer
   * - is-danger: atrasada
   *
   * @returns {import('lit').TemplateResult|string} Template do badge ou vazio em modo criação
   */
  _renderStatusLabel () {
    if (!this.isEditMode) return ''

    const status = calcularStatusLocal({
      dataPagamento: this.dataPagamento,
      dataVencimento: this.dataVencimento,
      tipo: this.selectedType
    })

    const config = {
      paga: { texto: 'Paga', classe: 'is-success' },
      recebida: { texto: 'Recebida', classe: 'is-success' },
      efetivada: { texto: 'Efetivada', classe: 'is-success' },
      a_vencer: { texto: 'A vencer', classe: 'is-warning' },
      atrasada: { texto: 'Atrasada', classe: 'is-danger' }
    }

    const { texto, classe } = config[status] || { texto: '', classe: '' }
    if (!texto) return ''

    return html`
      <div class="field mb-4">
        <span class="tag ${classe}" data-cy="label-status-transacao">${texto}</span>
      </div>
    `
  }

  _toggleRecurringEnd(e) {
    this.hasRecurringEnd = e.target.checked
  }

  _setRecurringEndType(type) {
    this.recurringEndType = type
  }

  _formatCurrency(e) {
    let value = e.target.value.replace(/\D/g, '')
    if (value === '') {
      e.target.value = ''
      return
    }
    
    const numericValue = parseInt(value) / 100
    e.target.value = numericValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })
    
    this.requestUpdate()

    // Se divisão está ativa, recalcular valores mantendo os percentuais definidos
    if (this.showSplits && this.splits.length > 0) {
      this._recalcularSplitsPorPercentual(numericValue)
    }
  }

  /**
   * Recalcula os valores dos splits com base nos percentuais existentes.
   * Usado quando o valor total da transação muda — mantém os percentuais
   * e ajusta os valores em centavos (sobra vai pro primeiro split).
   * @param {number} novoTotal - Novo valor total da transação
   * @private
   */
  _recalcularSplitsPorPercentual (novoTotal) {
    if (novoTotal <= 0 || this.splits.length === 0) return

    let centavosRestantes = Math.round(novoTotal * 100)
    const newSplits = this.splits.map((split, idx) => {
      const percentage = parseFloat(split.percentage) || 0
      let centavos = Math.floor((percentage / 100) * novoTotal * 100)
      return { ...split, _centavos: centavos }
    })

    // Somar centavos alocados
    const totalAlocado = newSplits.reduce((sum, s) => sum + s._centavos, 0)
    let sobra = centavosRestantes - totalAlocado

    // Atribuir sobra ao primeiro split
    if (sobra !== 0 && newSplits.length > 0) {
      newSplits[0]._centavos += sobra
    }

    // Converter centavos para valor e limpar campo auxiliar
    this.splits = newSplits.map(split => {
      const valor = split._centavos / 100
      const { _centavos, ...rest } = split
      return { ...rest, value: valor.toFixed(2) }
    })
  }

  _formatSplitValue(e, index) {
    // Delegado para _onSplitValueInput — manter para compatibilidade com referências externas
    this._onSplitValueInput(e, index)
  }

  _getTotalValue() {
    const valueInput = this.renderRoot.querySelector('[data-cy="input-valor"]')
    const value = valueInput?.value || ''
    const numericValue = value.replace(/[^\d,]/g, '').replace(',', '.')
    return parseFloat(numericValue || 0)
  }

  _calculateValue(split) {
    const total = this._getTotalValue()
    if (this.splitMode === 'percentage') {
      return ((parseFloat(split.percentage) || 0) * total / 100).toFixed(2)
    }
    return split.value || 0
  }

  _calculatePercentage(split) {
    const total = this._getTotalValue()
    if (this.splitMode === 'value' && total > 0) {
      return ((parseFloat(split.value) || 0) * 100 / total).toFixed(2)
    }
    return split.percentage || 0
  }

  /**
   * Retorna as tags filtradas pelo texto digitado no input.
   * Exclui tags já adicionadas à transação.
   * @returns {Array} Tags disponíveis filtradas
   * @private
   */
  _getFilteredTags () {
    const input = this.renderRoot?.querySelector('#tag-input')
    const filter = (input?.value || '').trim().toLowerCase()
    return this.tagsDisponiveis.filter(tag => {
      // Não mostrar tags já adicionadas à transação
      if (this.tags.includes(tag.nome)) return false
      // Filtrar pelo texto digitado
      if (filter && !tag.nome.toLowerCase().includes(filter)) return false
      return true
    })
  }

  /**
   * Abre a dropdown de tags ao focar no input.
   * @private
   */
  _onTagInputFocus () {
    this._showTagDropdown = true
  }

  /**
   * Fecha a dropdown de tags com pequeno delay para permitir cliques nas pills.
   * @private
   */
  _onTagInputBlur () {
    // Delay para permitir que o click na pill seja processado antes de fechar
    setTimeout(() => {
      this._showTagDropdown = false
    }, 200)
  }

  /**
   * Handler de input no campo de tags — atualiza filtro e exibe dropdown.
   * @private
   */
  _onTagInput () {
    this._showTagDropdown = true
    this.requestUpdate()
  }

  /**
   * Seleciona uma tag da dropdown customizada — preenche o input com o nome.
   * Não adiciona à transação diretamente. O usuário confirma via botão ✓ ou edita via ✏️.
   * @param {Object} tag - Objeto da tag selecionada (com nome e cor)
   * @private
   */
  _selectTagFromDropdown (tag) {
    const input = this.renderRoot.querySelector('#tag-input')
    if (input) input.value = tag.nome
    this._showTagDropdown = false
    this.requestUpdate()
  }

  async _addTag() {
    const input = this.renderRoot.querySelector('#tag-input')
    const tagText = input.value.trim()
    
    if (!tagText) return
    
    if (this.tags.includes(tagText)) {
      input.value = ''
      this._showTagDropdown = false
      return
    }
    
    // Verificar se a tag já existe no cadastro
    const tagExistente = this.tagsDisponiveis.find(tag => tag.nome.toLowerCase() === tagText.toLowerCase())
    
    if (tagExistente) {
      // Tag existe, apenas adicionar à transação
      this.tags = [...this.tags, tagText]
      input.value = ''
      this._showTagDropdown = false
    } else {
      // Tag não existe, abrir modal para criar
      this._showTagDropdown = false
      const modal = this.renderRoot.querySelector('modal-tag')
      modal.show(null) // null para modo de criação
      // Preencher o nome após abrir
      setTimeout(() => {
        const nameInput = modal.renderRoot.querySelector('[data-cy="input-nome"]')
        if (nameInput) nameInput.value = tagText
      }, 0)
    }
  }

  /**
   * Verifica se o texto no input de tags corresponde a uma tag pré-existente.
   * Usado para habilitar/desabilitar o botão de edição.
   * @returns {boolean} True se o input contém o nome de uma tag cadastrada
   * @private
   */
  _isTagExistente () {
    const input = this.renderRoot?.querySelector('#tag-input')
    const tagNome = input?.value?.trim()
    if (!tagNome) return false
    return this.tagsDisponiveis.some(t => t.nome.toLowerCase() === tagNome.toLowerCase())
  }

  _editTag() {
    const input = this.renderRoot.querySelector('#tag-input')
    const tagNome = input.value.trim()
    if (!tagNome) return
    
    const tag = this.tagsDisponiveis.find(t => t.nome.toLowerCase() === tagNome.toLowerCase())
    if (tag) {
      const modal = this.renderRoot.querySelector('modal-tag')
      modal.show(tag)
    }
  }

  async _handleTagSave(e) {
    const { id, nome, cor } = e.detail
    
    try {
      if (id) {
        // Edição
        await this.tagCacheService.atualizar(id, { nome, cor })
      } else {
        // Criação
        await this.tagCacheService.criar({ nome, cor })
        
        // Adicionar à lista de tags selecionadas apenas na criação
        if (nome && !this.tags.includes(nome)) {
          this.tags = [...this.tags, nome]
        }
      }
      
      // Atualizar lista local
      this.tagsDisponiveis = await this.tagCacheService.listar()
      this.requestUpdate()
      
      // Limpar input
      const input = this.renderRoot.querySelector('#tag-input')
      if (input) input.value = ''
    } catch (error) {
      console.error('Erro ao salvar tag:', error)
    }
  }

  /**
   * Recarrega a lista de tags quando o modal-tag fecha.
   * O ModalSaveMixin fecha após salvar com sucesso — isso garante que a lista
   * é atualizada independente de qual evento/caminho disparou o salvamento.
   * Detecta nova tag e a adiciona automaticamente à transação.
   * Limpa o input de tags apenas após criação (não após edição).
   * @private
   */
  async _onTagModalClosed () {
    const tagsAntes = new Set((this.tagsDisponiveis || []).map(t => t.id))
    this.tagsDisponiveis = await this.tagCacheService.listar()
    this.requestUpdate()

    // Detecta tag nova e adiciona automaticamente à transação
    const novaTag = this.tagsDisponiveis.find(t => !tagsAntes.has(t.id))
    if (novaTag && novaTag.nome && !this.tags.includes(novaTag.nome)) {
      this.tags = [...this.tags, novaTag.nome]
      this.requestUpdate()

      // Limpar input de tags apenas após criação bem-sucedida
      const input = this.renderRoot.querySelector('#tag-input')
      if (input) input.value = ''
    }
    // Se não é criação (edição ou cancelamento), mantém o input intacto
  }

  _removeTag(tagToRemove) {
    this.tags = this.tags.filter(tag => tag !== tagToRemove)
  }

  _handleTagKeyPress(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      this._addTag()
    }
  }

  _getInstallmentValue() {
    const installmentInput = this.renderRoot.querySelector('[data-cy="input-parcelas"]')
    const installments = parseInt(installmentInput?.value || 1)
    const total = this._getTotalValue()
    return (total / installments).toFixed(2)
  }

  _addConta() {
    const modal = this.renderRoot.querySelector('modal-conta')
    modal.show()
  }

  _editConta() {
    const select = this.renderRoot.querySelector('[data-cy="select-conta"]')
    const contaId = select.value
    if (!contaId) return
    
    const conta = this.contas.find(c => c.id === contaId)
    if (conta) {
      const modal = this.renderRoot.querySelector('modal-conta')
      modal.show(conta)
    }
  }

  async _handleContaSave(e) {
    const { id, nome, saldo, saldoInicial, saldoAtual, tipo, contaAssociada } = e.detail
    
    try {
      let novaConta
      if (id) {
        // Edição
        const contaData = { nome, tipo, saldoInicial, saldoAtual, contaAssociada }
        novaConta = await this.contaCacheService.atualizar(id, contaData)
      } else {
        // Criação
        const contaData = { nome, tipo, saldoInicial: saldo, contaAssociada }
        novaConta = await this.contaCacheService.criar(contaData)
      }
      
      // Atualizar lista local
      this.contas = await this.contaCacheService.listar()
      this.requestUpdate()
      
      // Selecionar a conta criada/editada
      setTimeout(() => {
        const select = this.renderRoot.querySelector('[data-cy="select-conta"]')
        if (select && novaConta) {
          select.value = novaConta.id
        }
      }, 100)
    } catch (error) {
      console.error('Erro ao salvar conta:', error)
    }
  }

  _addCategoria() {
    const modal = this.renderRoot.querySelector('modal-categoria')
    const tipoCategoria = this.selectedType === 'despesa' ? 'Despesa' : 'Receita'
    modal.show(null, tipoCategoria)
  }

  _editCategoria() {
    const select = this.renderRoot.querySelector('[data-cy="select-categoria"]')
    const categoriaId = select.value
    if (!categoriaId) return
    
    const categoria = this.categorias.find(c => c.id === categoriaId)
    if (categoria) {
      const modal = this.renderRoot.querySelector('modal-categoria')
      const tipoCategoria = this.selectedType === 'despesa' ? 'Despesa' : 'Receita'
      modal.show(categoria, tipoCategoria)
    }
  }

  async _handleCategoriaSave(e) {
    const { id, nome, emoji, tipo } = e.detail
    
    try {
      let novaCategoria
      if (id) {
        // Edição
        novaCategoria = await this.categoriaCacheService.atualizar(id, { nome, emoji, tipo })
      } else {
        // Criação
        novaCategoria = await this.categoriaCacheService.criar({ nome, emoji, tipo })
      }
      
      // Atualizar lista local
      this.categorias = await this.categoriaCacheService.listar()
      this.requestUpdate()
      
      // Selecionar a categoria criada/editada
      setTimeout(() => {
        const select = this.renderRoot.querySelector('[data-cy="select-categoria"]')
        if (select && novaCategoria) {
          select.value = novaCategoria.id
        }
      }, 100)
    } catch (error) {
      console.error('Erro ao salvar categoria:', error)
    }
  }

  _addResponsavel() {
    this._splitIndexForNewResponsavel = null
    const modal = this.renderRoot.querySelector('modal-responsavel')
    modal.show()
  }

  /**
   * Abre modal para criar responsável a partir de um split específico.
   * Armazena o índice do split para auto-selecionar após criação.
   * @param {number} splitIndex - Índice do split que solicitou a criação
   * @private
   */
  _addResponsavelFromSplit (splitIndex) {
    this._splitIndexForNewResponsavel = splitIndex
    const modal = this.renderRoot.querySelector('modal-responsavel')
    modal.show()
  }

  async _getCurrentUserEmail() {
    try {
      // Usar Firebase Auth diretamente
      const { getAuth } = await import('firebase/auth')
      const auth = getAuth()
      this._processAuthUser(auth.currentUser)
    } catch (error) {
      console.warn('Erro ao obter email do usuário:', error)
      // Fallback para localStorage quando Firebase não está disponível
      this._processAuthUser(null)
    }
  }

  /**
   * Processa o usuário autenticado para extrair email
   * Usa localStorage como fallback quando não há usuário Firebase
   * @param {Object|null} user - Objeto do usuário Firebase ou null
   */
  _processAuthUser(user) {
    if (user && user.email) {
      this.currentUserEmail = user.email
      localStorage.setItem('userEmail', user.email)
      this.requestUpdate()
      return
    }
    // Fallback para localStorage quando Firebase não tem usuário
    const storedEmail = localStorage.getItem('userEmail') || ''
    if (storedEmail) {
      this.currentUserEmail = storedEmail
    }
  }

  _isCurrentUser(responsavel) {
    if (!this.currentUserEmail) {
      this._getCurrentUserEmail()
    }
    return responsavel.email === this.currentUserEmail
  }

  async _handleResponsavelSave(e) {
    const { id, nome, email, telefone, apelido } = e.detail
    const responsavelData = { nome, email, telefone, apelido }
    
    try {
      if (id) {
        // Edição
        await this.responsavelCacheService.atualizar(id, responsavelData)
      } else {
        // Criação
        await this.responsavelCacheService.criar(responsavelData)
      }
      
      // Atualizar lista local
      this.responsaveis = await this.responsavelCacheService.listar()
      this.requestUpdate()
    } catch (error) {
      console.error('Erro ao salvar responsável:', error)
    }
  }

  /**
   * Recarrega a lista de responsáveis quando o modal-responsavel fecha.
   * O ModalSaveMixin fecha após salvar com sucesso — isso garante que a lista
   * é atualizada independente de qual evento/caminho disparou o salvamento.
   * Detecta novo responsável e seleciona automaticamente:
   * - Se veio de um split (_splitIndexForNewResponsavel != null), seleciona no split
   * - Senão, seleciona no campo principal de responsável
   * @private
   */
  async _onResponsavelModalClosed () {
    const responsaveisAntes = new Set((this.responsaveis || []).map(r => r.id))
    this.responsaveis = await this.responsavelCacheService.listar()
    this.requestUpdate()

    // Detecta responsável novo e seleciona automaticamente
    const novoResp = this.responsaveis.find(r => !responsaveisAntes.has(r.id))
    if (novoResp) {
      if (this._splitIndexForNewResponsavel !== null && this._splitIndexForNewResponsavel !== undefined) {
        // Veio de um split — selecionar no split correspondente
        const idx = this._splitIndexForNewResponsavel
        if (idx >= 0 && idx < this.splits.length) {
          this._updateSplit(idx, 'person', novoResp.id)
        }
        this._splitIndexForNewResponsavel = null
      } else {
        // Veio do campo principal — selecionar no responsável da transação
        this.responsiblePerson = novoResp.id
        await this.updateComplete
        const select = this.renderRoot.querySelector('[data-cy="select-responsavel"]')
        if (select) {
          select.value = novoResp.id
        }
      }
    }
    this._splitIndexForNewResponsavel = null
  }

  _editResponsavel() {
    const responsavelId = this.responsiblePerson
    if (!responsavelId) return
    
    const responsavel = this.responsaveis.find(r => r.id === responsavelId)
    if (responsavel) {
      const modal = this.renderRoot.querySelector('modal-responsavel')
      const isCurrentUser = this._isCurrentUser(responsavel)
      console.log('Debug:', { responsavel, currentUserEmail: this.currentUserEmail, isCurrentUser })
      modal.show(responsavel, isCurrentUser)
    }
  }

  async _loadData() {
    try {
      const [contas, categorias, responsaveis, tags] = await Promise.all([
        this.contaCacheService.listar(), // Cache inteligente
        this.categoriaCacheService.listar(), // Cache inteligente
        this.responsavelCacheService.listar(), // Cache inteligente
        this.tagCacheService.listar() // Cache inteligente
      ])
      
      this.contas = contas
      this.categorias = categorias
      this.responsaveis = responsaveis
      this.tagsDisponiveis = tags
      
      // Definir responsável padrão como responsável principal (usuário logado)
      if (!this.responsiblePerson && responsaveis.length > 0) {
        const currentUserEmail = localStorage.getItem('userEmail') || ''
        const responsavelPrincipal = responsaveis.find(r => r.email === currentUserEmail)
        this.responsiblePerson = responsavelPrincipal ? responsavelPrincipal.id : responsaveis[0].id
      }
      
      // Inicializar splits se vazio
      if (this.splits.length === 0 && responsaveis.length > 0) {
        const currentUserEmail = localStorage.getItem('userEmail') || ''
        const currentUser = responsaveis.find(r => r.email === currentUserEmail)
        const defaultResponsavel = currentUser ? currentUser.id : responsaveis[0].id
        this.splits = [{ person: defaultResponsavel, percentage: 50, value: 0 }]
      }
      
      this.requestUpdate()
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    }
  }

  _editResponsavelFromSplit(responsavelId) {
    const responsavel = this.responsaveis.find(r => r.id === responsavelId)
    if (responsavel) {
      const modal = this.renderRoot.querySelector('modal-responsavel')
      const isCurrentUser = this._isCurrentUser(responsavel)
      modal.show(responsavel, isCurrentUser)
    }
  }

  _setupFieldErrorClearListeners() {
    // Configurar listeners para limpar erros quando usuário começar a digitar
    this.addEventListener('input', (e) => {
      if (e.target.matches('.input, .select select')) {
        this._clearFieldError(e.target)
        // Remover mensagem de erro inline associada ao campo
        this._removeFieldInlineError(e.target)
      }
      // Limpar erro de salvamento do mixin ao interagir com qualquer campo
      this._clearError()
    })
    
    this.addEventListener('change', (e) => {
      if (e.target.matches('.input, .select select')) {
        this._clearFieldError(e.target)
        // Remover mensagem de erro inline associada ao campo
        this._removeFieldInlineError(e.target)
      }
      // Limpar erro de salvamento do mixin ao interagir com qualquer campo
      this._clearError()
    })
  }

  /**
   * Remove a mensagem de erro inline associada a um campo específico
   * Busca elementos .help.is-danger dentro do container do campo
   * @param {HTMLElement} field - Elemento do campo cujo erro deve ser removido
   */
  _removeFieldInlineError(field) {
    const fieldContainer = field.closest('.field')
    if (fieldContainer) {
      const errorMsg = fieldContainer.querySelector('.help.is-danger[data-cy]')
      if (errorMsg) errorMsg.remove()
    }
  }

  _clearFieldError(field) {
    // Remove destaque de erro de um campo específico
    field.classList.remove('is-danger')
    field.style.removeProperty('border-color')
    field.style.removeProperty('box-shadow')
  }

  _clearFieldErrors() {
    // Remove destaque de erro de todos os campos
    const fields = this.renderRoot.querySelectorAll('.input, .select select')
    fields.forEach(field => {
      field.classList.remove('is-danger')
      field.style.removeProperty('border-color')
      field.style.removeProperty('box-shadow')
    })
    // Limpar mensagens de erro inline de transferência
    this._clearTransferenciaErrors()
  }

  /**
   * Limpa todas as mensagens de erro inline dos campos de transferência
   * Remove os elementos de ajuda com classe is-danger criados pela validação
   */
  _clearTransferenciaErrors() {
    const errorMessages = this.renderRoot.querySelectorAll('.help.is-danger[data-cy]')
    errorMessages.forEach(el => el.remove())
  }

  /**
   * Exibe mensagem de erro inline abaixo de um campo específico
   * Utiliza classes Bulma (help is-danger) para estilização consistente
   * @param {HTMLElement} field - Elemento do campo que contém o erro
   * @param {string} message - Mensagem de erro a ser exibida
   * @param {string} dataCy - Atributo data-cy para identificação em testes E2E
   */
  _showFieldError(field, message, dataCy) {
    // Adicionar classe is-danger ao campo
    field.classList.add('is-danger')
    field.style.setProperty('border-color', 'var(--danger, #EF4444)', 'important')
    field.style.setProperty('box-shadow', '0 0 0 0.125em rgba(239, 68, 68, 0.25)', 'important')

    // Criar elemento de mensagem de erro inline
    const helpEl = document.createElement('p')
    helpEl.classList.add('help', 'is-danger')
    helpEl.setAttribute('data-cy', dataCy)
    helpEl.textContent = message

    // Inserir mensagem após o container do campo (.control ou .select)
    const fieldContainer = field.closest('.field') || field.closest('.control')
    if (fieldContainer) {
      fieldContainer.appendChild(helpEl)
    }
  }

  /**
   * Valida os campos específicos de transferência antes do envio ao backend
   * Verifica: conta origem selecionada, conta destino selecionada,
   * contas diferentes e valor positivo
   * @returns {boolean} true se a validação passou, false se há erros
   */
  _validateTransferencia() {
    let isValid = true

    const contaOrigemSelect = this.renderRoot.querySelector('[data-cy="select-conta-origem"]')
    const contaDestinoSelect = this.renderRoot.querySelector('[data-cy="select-conta-destino"]')
    const valorInput = this.renderRoot.querySelector('[data-cy="input-valor"]')
    const valor = this._getTotalValue()

    // Validar conta origem selecionada
    if (!this.contaOrigemId) {
      this._showFieldError(contaOrigemSelect, 'Selecione a conta de origem', 'erro-conta-origem')
      isValid = false
    }

    // Validar conta destino selecionada
    if (!this.contaDestinoId) {
      this._showFieldError(contaDestinoSelect, 'Selecione a conta de destino', 'erro-conta-destino')
      isValid = false
    }

    // Validar contas diferentes (apenas se ambas estão selecionadas)
    if (this.contaOrigemId && this.contaDestinoId && this.contaOrigemId === this.contaDestinoId) {
      this._showFieldError(contaDestinoSelect, 'Conta origem e destino devem ser diferentes', 'erro-contas-iguais')
      isValid = false
    }

    // Validar valor positivo
    if (!valorInput.value || valor <= 0) {
      this._showFieldError(valorInput, 'Valor deve ser positivo', 'erro-valor-transferencia')
      isValid = false
    }

    return isValid
  }

  _highlightRequiredFields(emptyFields) {
    // Adiciona destaque vermelho aos campos obrigatórios vazios
    emptyFields.forEach(field => {
      field.classList.add('is-danger')
      // Força o estilo mesmo com temas usando token --danger
      field.style.setProperty('border-color', 'var(--danger, #EF4444)', 'important')
      field.style.setProperty('box-shadow', '0 0 0 0.125em rgba(239, 68, 68, 0.25)', 'important')
    })
  }

  /**
   * Executa a operação de salvamento delegando ao ModalSaveMixin.
   *
   * Valida campos obrigatórios, monta o payload e delega ao _executeSave
   * do mixin que gerencia loading, timeout de 30s, eventos de ciclo de vida
   * (cache-operation-started/finished) e tratamento de erro inline.
   *
   * Guard contra clique duplo: se já está salvando, ignora chamadas subsequentes.
   * @returns {Promise<void>}
   */
  async _handleSave() {
    if (this._saving) return

    // Limpar erros anteriores
    this._clearFieldErrors()

    const form = this.renderRoot.querySelector('form')
    const formData = new FormData(form)
    
    // Validar campos obrigatórios
    const valorInput = this.renderRoot.querySelector('[data-cy="input-valor"]')
    const descricaoInput = this.renderRoot.querySelector('[data-cy="input-descricao"]')
    const categoriaSelect = this.renderRoot.querySelector('[data-cy="select-categoria"]')
    const contaSelect = this.renderRoot.querySelector('[data-cy="select-conta"]')
    const dataInput = this.renderRoot.querySelector('[data-cy="input-data"]')

    const valorValue = valorInput.value
    const descricao = descricaoInput.value
    const categoriaId = categoriaSelect ? categoriaSelect.value : ''
    const contaId = contaSelect ? contaSelect.value : ''
    const data = dataInput.value

    // Converter valor formatado para número
    const valor = this._getTotalValue()

    // Validação específica para transferências (client-side)
    if (this.selectedType === 'transferencia') {
      const transferenciaValida = this._validateTransferencia()
      
      // Validar campos comuns (descrição e data) para transferências
      const emptyFields = []
      if (!descricao) emptyFields.push(descricaoInput)
      if (!data) emptyFields.push(dataInput)

      if (emptyFields.length > 0) {
        this._highlightRequiredFields(emptyFields)
        emptyFields[0].focus()
        alert('Preencha todos os campos obrigatórios')
        return
      }

      if (!transferenciaValida) {
        return
      }
    } else {
      // Validação padrão para receitas e despesas
      // Identificar campos obrigatórios vazios
      const emptyFields = []
      if (!valorValue || valor <= 0) emptyFields.push(valorInput)
      if (!descricao) emptyFields.push(descricaoInput)
      if (!categoriaId) emptyFields.push(categoriaSelect)
      if (!contaId) emptyFields.push(contaSelect)
      if (!data) emptyFields.push(dataInput)

      if (emptyFields.length > 0) {
        // Destacar campos com erro
        this._highlightRequiredFields(emptyFields)
        
        // Focar no primeiro campo vazio
        emptyFields[0].focus()
        
        alert('Preencha todos os campos obrigatórios')
        return
      }
    }

    // Validar divisão se estiver ativa
    if (this.showSplits) {
      // Validar que todos os splits têm responsável selecionado
      const splitSemResponsavel = this.splits.some(split => !split.person)
      if (splitSemResponsavel) {
        alert('Todos os responsáveis da divisão devem ser selecionados.')
        return
      }

      if (this.splitMode === 'percentage') {
        const totalPercentage = this._getTotalPercentage()
        if (Math.abs(totalPercentage - 100) > 0.01) {
          alert(`A divisão deve totalizar 100%. Atual: ${totalPercentage}%`)
          return
        }
      } else if (this.splitMode === 'value') {
        const totalSplitValue = this._getTotalSplitValue()
        if (Math.abs(totalSplitValue - valor) > 0.01) {
          const diff = totalSplitValue - valor
          const msg = diff > 0
            ? `A divisão excede o valor da transação em ${Math.abs(diff).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.`
            : `Faltam ${Math.abs(diff).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} para completar a divisão.`
          alert(msg)
          return
        }
      }
    }

    // Converter data para formato brasileiro
    const [year, month, day] = data.split('-')
    const dataFormatada = `${day}/${month}/${year}`

    // Montar payload da transação
    const dados = this._montarPayload({
      descricao,
      valor,
      dataFormatada,
      contaId,
      categoriaId
    })

    // Determinar operação e ID com base no modo de edição
    const operacao = this.isEditMode ? 'atualizar' : 'criar'
    const id = this.isEditMode ? this.transactionData.id : undefined

    // Delegar ao mixin que gerencia loading, timeout e erro inline
    const result = await this._executeSave(this.transacaoService, operacao, dados, id)

    // Pós-sucesso: invalidar cache de contas se for transferência
    // Transferências alteram saldos de 2 contas simultaneamente
    if (result && this.selectedType === 'transferencia') {
      await this._invalidarCacheAposTransferencia()
    }
  }

  /**
   * Handler de submit do formulário — delega para _handleSave.
   * Previne o comportamento padrão do formulário e redireciona
   * para o fluxo de salvamento via ModalSaveMixin.
   * @param {Event} e - Evento de submit do formulário
   */
  async _handleSubmit(e) {
    e.preventDefault()
    await this._handleSave()
  }

  /**
   * Monta o payload da transação de acordo com o tipo selecionado
   * Para transferências: inclui infoTransferencia e usa contaOrigemId como contaId
   * Para receitas/despesas: inclui categoriaId, responsavelId e demais campos
   * @param {Object} params - Parâmetros extraídos do formulário
   * @param {string} params.descricao - Descrição da transação
   * @param {number} params.valor - Valor numérico da transação
   * @param {string} params.dataFormatada - Data no formato dd/mm/yyyy
   * @param {string} params.contaId - ID da conta selecionada (receita/despesa)
   * @param {string} params.categoriaId - ID da categoria selecionada
   * @returns {Object} Payload pronto para envio ao backend
   */
  _montarPayload ({ descricao, valor, dataFormatada, contaId, categoriaId }) {
    // Normaliza valor: string vazia ou nula → null, valor real → mantém
    const normalizarData = (val) => (val && val.trim() !== '') ? val : null

    // Para campos de data exibidos na UI (modalidade "a_prazo"), o DOM é a fonte de
    // verdade: reflete exatamente o que o usuário preencheu ou limpou, incluindo
    // limpeza programática via .fill('') no Playwright que pode não disparar @change
    // em inputs type="date" no Shadow DOM.
    // Quando o campo não está visível (modalidade "a_vista" etc.), usa o estado Lit.
    const inputDataPagamentoDom = this.renderRoot.querySelector('[data-cy="input-data-pagamento"]')
    const inputDataVencimentoDom = this.renderRoot.querySelector('[data-cy="input-data-vencimento"]')

    const camposDataVisiveis = this.modalidadePagamento === 'a_prazo'

    // dataPagamento: DOM tem prioridade quando campo está visível (captura limpeza do campo)
    // Quando campo oculto, usa estado Lit (modalidade a_vista define automaticamente)
    const dataPagamento = camposDataVisiveis
      ? normalizarData(inputDataPagamentoDom?.value)
      : this.dataPagamento

    // dataVencimento: DOM tem prioridade quando campo está visível
    const dataVencimento = camposDataVisiveis
      ? normalizarData(inputDataVencimentoDom?.value) ?? this.dataVencimento
      : this.dataVencimento

    // Payload específico para transferências
    // Não inclui categoriaId nem responsavelId conforme regra de negócio
    if (this.selectedType === 'transferencia') {
      return {
        descricao,
        valor: Math.abs(valor),
        tipo: 'transferencia',
        data: dataFormatada,
        contaId: this.contaOrigemId,
        modalidadePagamento: this.modalidadePagamento,
        dataVencimento: dataVencimento,
        dataPagamento: dataPagamento,
        infoTransferencia: {
          contaOrigemId: this.contaOrigemId,
          contaDestinoId: this.contaDestinoId
        },
        tagIds: this.tags.map(tagNome => {
          const tag = this.tagsDisponiveis.find(t => t.nome === tagNome)
          return tag?.id || null
        }).filter(Boolean),
        tagNomes: this.tags
      }
    }

    // Payload padrão para receitas e despesas
    return {
      descricao,
      valor: Math.abs(valor),
      tipo: this.selectedType,
      data: dataFormatada,
      contaId,
      categoriaId,
      modalidadePagamento: this.modalidadePagamento,
      dataVencimento: dataVencimento,
      dataPagamento: dataPagamento,
      tagIds: this.tags.map(tagNome => {
        const tag = this.tagsDisponiveis.find(t => t.nome === tagNome)
        return tag?.id || null
      }).filter(Boolean),
      tagNomes: this.tags,
      responsavelId: this.showSplits ? null : this.responsiblePerson,
      compartilhado: this.showSplits,
      divisoes: this.showSplits ? this.splits.map(split => ({
        responsavelId: split.person,
        valor: parseFloat(split.value) || 0,
        percentual: parseFloat(split.percentage) || 0
      })) : [],
      parcelado: this.isInstallment,
      infoParcelas: this.isInstallment ? {
        numeroParcelas: parseInt(this.renderRoot.querySelector('[data-cy="input-parcelas"]')?.value || 1)
      } : {},
      recorrente: this.isRecurring,
      infoRecorrencia: this.isRecurring ? {
        frequencia: this.renderRoot.querySelector('[data-cy="select-frequencia"]')?.value || 'monthly',
        temTermino: this.hasRecurringEnd,
        tipoTermino: this.recurringEndType,
        numeroOcorrencias: this.recurringEndType === 'count' ? parseInt(this.renderRoot.querySelector('[data-cy="input-ocorrencias"]')?.value || 0) : null,
        dataFinal: this.recurringEndType === 'date' ? this.renderRoot.querySelector('[data-cy="input-data-final"]')?.value : null
      } : {}
    }
  }

  /**
   * Invalida cache de contas após transferência bem-sucedida
   * Transferências alteram saldos de 2 contas (origem e destino),
   * então é necessário forçar recarregamento dos dados de contas
   * Dispara evento 'contas-cache-updated' para atualizar dashboard e outros componentes
   * @returns {Promise<void>}
   */
  async _invalidarCacheAposTransferencia () {
    try {
      // Recarregar cache de contas (saldos mudaram em 2 contas)
      await this.contaCacheService.recarregar()

      // Disparar evento para atualizar dashboard e outros componentes que escutam
      window.dispatchEvent(new CustomEvent('contas-cache-updated'))
    } catch (error) {
      console.error('Erro ao invalidar cache de contas após transferência:', error)
    }
  }

  render() {
    return html`
      <div 
        class="modal ${this.open ? 'is-active' : ''}" 
        @click="${this._handleBackdropClick}"
        style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 20000;
          display: ${this.open ? 'flex' : 'none'};
          align-items: center;
          justify-content: center;
        "
      >
        <div 
          class="card"
          @click="${this._stopPropagation}"
          style="
            width: 90%;
            max-width: 500px;
            max-height: 85vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border-radius: var(--radius-card, 20px);
            box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));
          "
        >
          <header class="card-header" style="flex-shrink: 0; position: relative; z-index: 2; box-shadow: var(--shadow-header-modal, 0 4px 12px rgba(0,0,0,0.3));">
            ${this.renderBotaoVoltarIOS()}
            <p class="card-header-title" data-cy="modal-transacao-title">
              ${this.isEditMode ? 'Editar Transação' : 'Adicionar Transação'}
            </p>
            ${this.renderBotaoFechar('modal-transacao-close')}
          </header>

          <!-- Zona 2: Conteúdo com scroll independente -->
          <div class="card-content custom-scrollbar" style="
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
            min-height: 0;
          ">
            <form class="space-y-4" @submit="${this._handleSubmit}">
              <!-- Tipo (primeiro campo para definir contexto do formulário) -->
              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Tipo</label>
                <div class="buttons is-fullwidth" style="gap: 0.5rem;">
                  <button 
                    type="button" 
                    class="button flex-1 ${this.selectedType === 'despesa' ? 'is-danger' : 'is-danger is-light'}" 
                    data-cy="btn-tipo-despesa"
                    @click="${() => this._selectType('despesa')}"
                  >
                    Despesa
                  </button>
                  <button 
                    type="button" 
                    class="button flex-1 ${this.selectedType === 'receita' ? 'is-success' : 'is-success is-light'}" 
                    data-cy="btn-tipo-receita"
                    @click="${() => this._selectType('receita')}"
                  >
                    Receita
                  </button>
                  <button 
                    type="button" 
                    class="button flex-1 ${this.selectedType === 'transferencia' ? 'is-link' : 'is-link is-light'}" 
                    data-cy="btn-tipo-transferencia"
                    @click="${() => this._selectType('transferencia')}"
                  >
                    Transferência
                  </button>
                </div>
              </div>

              <!-- Data -->
              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Data *</label>
                <div class="control has-icons-left">
                  <input
                    class="input"
                    type="date"
                    value="${new Date().toISOString().split('T')[0]}"
                    data-cy="input-data"
                  />
                  <span class="icon is-left">
                    <i class="fas fa-calendar"></i>
                  </span>
                </div>
              </div>

              <!-- Valor -->
              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Valor *</label>
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    placeholder="R$ 0,00"
                    required
                    data-cy="input-valor"
                    @input="${this._formatCurrency}"
                  />
                </div>
              </div>

              <!-- Descrição -->
              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Descrição *</label>
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    placeholder="Ex: Padaria"
                    required
                    data-cy="input-descricao"
                  />
                </div>
              </div>

              <!-- Categoria (oculto quando tipo = transferência) -->
              ${this.selectedType !== 'transferencia' ? html`
              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Categoria *</label>
                <div class="field has-addons">
                  <div class="control is-expanded">
                    <div class="select is-fullwidth">
                      <select required data-cy="select-categoria">
                        <option value="">Selecione uma categoria</option>
                        ${this.categorias
                          .filter(categoria => categoria.tipo === (this.selectedType === 'despesa' ? 'Despesa' : 'Receita'))
                          .map(categoria => html`
                            <option value="${categoria.id}">${categoria.emoji} ${categoria.nome}</option>
                          `)}
                      </select>
                    </div>
                  </div>
                  <div class="control">
                    <button type="button" class="button" data-cy="btn-add-categoria" @click="${this._addCategoria}">
                      <span class="icon is-small">
                        <i class="fas fa-plus"></i>
                      </span>
                    </button>
                  </div>
                  <div class="control">
                    <button type="button" class="button" data-cy="btn-edit-categoria" @click="${this._editCategoria}">
                      <span class="icon is-small">
                        <i class="fas fa-pen"></i>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              ` : ''}

              <!-- Conta Origem e Conta Destino (exibido apenas quando tipo = transferência) -->
              ${this.selectedType === 'transferencia' ? html`
              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Conta Origem *</label>
                <div class="control">
                  <div class="select is-fullwidth">
                    <select required data-cy="select-conta-origem" @change="${(e) => { this.contaOrigemId = e.target.value }}">
                      <option value="">Selecione a conta de origem</option>
                      ${this._getContasAtivas().map(conta => html`
                        <option value="${conta.id}" ?selected="${conta.id === this.contaOrigemId}">${conta.nome}</option>
                      `)}
                    </select>
                  </div>
                </div>
              </div>

              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Conta Destino *</label>
                <div class="control">
                  <div class="select is-fullwidth">
                    <select required data-cy="select-conta-destino" @change="${(e) => { this.contaDestinoId = e.target.value }}">
                      <option value="">Selecione a conta de destino</option>
                      ${this._getContasAtivas().map(conta => html`
                        <option value="${conta.id}" ?selected="${conta.id === this.contaDestinoId}">${conta.nome}</option>
                      `)}
                    </select>
                  </div>
                </div>
              </div>
              ` : ''}

              <!-- Conta (oculto quando tipo = transferência, pois já tem conta origem/destino) -->
              ${this.selectedType !== 'transferencia' ? html`
              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Conta *</label>
                <div class="field has-addons">
                  <div class="control is-expanded">
                    <div class="select is-fullwidth">
                      <select required data-cy="select-conta" .value="${this.selectedContaId || ''}">
                        <option value="">Selecione uma conta</option>
                        ${this.contas.map(conta => html`
                          <option value="${conta.id}" ?selected="${conta.id === this.selectedContaId}">${conta.nome}</option>
                        `)}
                      </select>
                    </div>
                  </div>
                  <div class="control">
                    <button type="button" class="button" data-cy="btn-add-conta" @click="${this._addConta}">
                      <span class="icon is-small">
                        <i class="fas fa-plus"></i>
                      </span>
                    </button>
                  </div>
                  <div class="control">
                    <button type="button" class="button" data-cy="btn-edit-conta" @click="${this._editConta}">
                      <span class="icon is-small">
                        <i class="fas fa-pen"></i>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              ` : ''}

              <!-- Responsável (oculto quando tipo = transferência ou divisão ativa) -->
              ${!this.showSplits && this.selectedType !== 'transferencia' ? html`
                <div class="field mb-4">
                  <label class="label is-size-7 has-text-weight-medium">Responsável</label>
                  <div class="field has-addons">
                    <div class="control is-expanded">
                      <div class="select is-fullwidth">
                        <select data-cy="select-responsavel" @change="${(e) => { this.responsiblePerson = e.target.value; this.requestUpdate(); }}">
                          ${this.responsaveis.map(responsavel => html`
                            <option value="${responsavel.id}" ?selected="${responsavel.id === this.responsiblePerson}">${responsavel.nomeExibicao || responsavel.nome}</option>
                          `)}
                        </select>
                      </div>
                    </div>
                    <div class="control">
                      <button type="button" class="button" data-cy="btn-add-responsavel" @click="${this._addResponsavel}">
                        <span class="icon is-small">
                          <i class="fas fa-plus"></i>
                        </span>
                      </button>
                    </div>
                    <div class="control">
                      <button type="button" class="button" data-cy="btn-edit-responsavel" @click="${this._editResponsavel}">
                        <span class="icon is-small">
                          <i class="fas fa-pen"></i>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              ` : ''}

              <!-- Tags -->
              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Tags</label>
                <div class="field has-addons mb-2">
                  <div class="control is-expanded" style="position: relative;">
                    <input
                      id="tag-input"
                      class="input"
                      type="text"
                      placeholder="Digite ou selecione uma tag..."
                      autocomplete="off"
                      data-cy="input-tag"
                      @keypress="${this._handleTagKeyPress}"
                      @input="${this._onTagInput}"
                      @focus="${this._onTagInputFocus}"
                      @blur="${this._onTagInputBlur}"
                    />
                    ${this._showTagDropdown && this._getFilteredTags().length > 0 ? html`
                      <div class="tag-dropdown" data-cy="tag-dropdown" style="
                        position: absolute;
                        top: 100%;
                        left: 0;
                        right: 0;
                        z-index: 100;
                        max-height: 180px;
                        overflow-y: auto;
                        background-color: var(--bg-surface, #151C30);
                        border: 1px solid var(--border, #26304A);
                        border-top: none;
                        border-radius: 0 0 var(--radius-input, 12px) var(--radius-input, 12px);
                        padding: 0.5rem;
                        display: flex;
                        flex-wrap: wrap;
                        gap: 0.4rem;
                        box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));
                      ">
                        ${this._getFilteredTags().map(tag => html`
                          <span
                            class="tag is-medium"
                            data-cy="tag-dropdown-item"
                            style="
                              background-color: ${tag.cor || 'var(--primary, #6120D8)'};
                              color: white;
                              cursor: pointer;
                              border-radius: var(--radius-badge, 999px);
                              font-weight: 500;
                              transition: opacity 0.15s ease;
                            "
                            @mousedown="${() => this._selectTagFromDropdown(tag)}"
                            @mouseenter="${(e) => { e.target.style.opacity = '0.8' }}"
                            @mouseleave="${(e) => { e.target.style.opacity = '1' }}"
                          >
                            ${tag.nome}
                          </span>
                        `)}
                      </div>
                    ` : ''}
                  </div>
                  <div class="control">
                    <button type="button" class="button" data-cy="btn-adicionar-tag" @click="${this._addTag}">
                      <span class="icon is-small">
                        <i class="fas fa-check"></i>
                      </span>
                    </button>
                  </div>
                  <div class="control">
                    <button type="button" class="button" data-cy="btn-edit-tag" @click="${this._editTag}" ?disabled="${!this._isTagExistente()}">
                      <span class="icon is-small">
                        <i class="fas fa-pen"></i>
                      </span>
                    </button>
                  </div>
                </div>
                ${this.tags.length > 0 ? html`
                  <div class="tags">
                    ${this.tags.map(tag => {
                      const tagObj = this.tagsDisponiveis.find(t => t.nome === tag)
                      const cor = tagObj?.cor || 'var(--primary, #6120D8)'
                      return html`
                        <span class="tag" style="background-color: ${cor}; color: white;" data-cy="tag-item">
                          ${tag}
                          <button type="button" class="delete is-small" data-cy="btn-remover-tag" @click="${() => this._removeTag(tag)}"></button>
                        </span>
                      `
                    })}
                  </div>
                ` : ''}
              </div>

              <!-- Toggle de Modalidade de Pagamento (após Tags, antes dos painéis) -->
              ${!this.isEditMode || this.modalidadePagamento ? this._renderToggleModalidade() : ''}

              <!-- Campos condicionais de datas (vencimento e pagamento) -->
              ${this._renderCamposDatas()}

              <!-- Label de Status (apenas em modo edição) -->
              ${this._renderStatusLabel()}

              <!-- Dividir despesa entre responsáveis (oculto quando tipo = transferência) -->
              ${this.selectedType !== 'transferencia' ? html`
              <div class="field mb-4">
                <label class="checkbox">
                  <input type="checkbox" data-cy="checkbox-dividir-despesa" .checked="${this.showSplits}" @change="${this._toggleSplits}" /> 
                  Dividir ${this.selectedType} entre responsáveis
                </label>
              </div>

              <!-- Seção de Divisão -->
              ${this.showSplits ? html`
                <div class="box" style="padding: 1rem; border-radius: var(--radius-input, 12px); margin-bottom: 1rem; background-color: var(--bg-surface-secondary, #1D2740); overflow: hidden;">
                  <!-- Header da divisão: título + toggle modo -->
                  <div class="is-flex is-justify-content-space-between is-align-items-center mb-3" style="flex-wrap: wrap; gap: 0.5rem;">
                    <label class="label is-size-7 has-text-weight-medium mb-0">Tipo de divisão da ${this.selectedType}</label>
                    <div class="field has-addons mb-0">
                      <div class="control">
                        <button 
                          type="button" 
                          class="button ${this.splitMode === 'percentage' ? 'is-primary' : ''}"
                          style="min-height: 2.5rem; min-width: 2.5rem;"
                          @click="${() => this._setSplitMode('percentage')}"
                        >
                          <span>%</span>
                        </button>
                      </div>
                      <div class="control">
                        <button 
                          type="button" 
                          class="button ${this.splitMode === 'value' ? 'is-primary' : ''}"
                          style="min-height: 2.5rem; min-width: 2.5rem;"
                          @click="${() => this._setSplitMode('value')}"
                        >
                          <span>R$</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Cards de divisão — layout empilhado para mobile -->
                  ${this.splits.map((split, index) => html`
                    <div class="mb-3 p-3" style="border-radius: var(--radius-input, 12px); border: 1px solid var(--border, #26304A); background-color: var(--bg-surface, #151C30); box-sizing: border-box; width: 100%;">
                      
                      <!-- Linha 1: Responsável (select + botões) -->
                      <div class="field has-addons mb-3" style="max-width: 100%;">
                        <div class="control is-expanded" style="min-width: 0;">
                          <div class="select is-fullwidth">
                            <select 
                              data-cy="select-responsavel-split"
                              style="min-height: 2.5rem;"
                              @change="${(e) => this._updateSplit(index, 'person', e.target.value || null)}"
                            >
                              ${!split.person ? html`<option value="" selected>Selecione...</option>` : ''}
                              ${this.responsaveis
                                .filter(responsavel => {
                                  // Mostrar: o selecionado neste split + os não usados em outros splits
                                  if (responsavel.id === split.person) return true
                                  const usedInOtherSplits = this.splits.some((s, i) => i !== index && s.person === responsavel.id)
                                  return !usedInOtherSplits
                                })
                                .map(responsavel => html`
                                <option value="${responsavel.id}" ?selected="${split.person === responsavel.id}">
                                  ${responsavel.nomeExibicao || responsavel.nome}
                                </option>
                              `)}
                            </select>
                          </div>
                        </div>
                        <div class="control">
                          <button type="button" class="button" style="min-height: 2.5rem; min-width: 2.5rem;" data-cy="btn-add-responsavel-split" @click="${() => this._addResponsavelFromSplit(index)}">
                            <span class="icon">
                              <i class="fas fa-plus"></i>
                            </span>
                          </button>
                        </div>
                        <div class="control">
                          <button type="button" class="button" style="min-height: 2.5rem; min-width: 2.5rem;" data-cy="btn-edit-responsavel-split" @click="${() => this._editResponsavelFromSplit(split.person)}">
                            <span class="icon">
                              <i class="fas fa-pen"></i>
                            </span>
                          </button>
                        </div>
                      </div>

                      <!-- Linha 2: Valores (% e R$) + botão remover -->
                      <div class="is-flex is-align-items-center" style="gap: 0.5rem; max-width: 100%;">
                        <div style="flex: 1; min-width: 0;">
                          <input 
                            class="input" 
                            type="text" 
                            placeholder="${this.splitMode === 'percentage' ? '% ' : 'Percentual'}"
                            style="min-height: 2.5rem; ${this.splitMode !== 'percentage' ? 'background-color: var(--bg-surface-secondary, #1D2740); opacity: 0.7;' : ''}"
                            .value="${split.percentage ? split.percentage.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%' : ''}"
                            ?readonly="${this.splitMode === 'value'}"
                            @input="${(e) => this._onSplitPercentageInput(e, index)}"
                            @focus="${(e) => e.target.select()}"
                          />
                        </div>
                        <div style="flex: 1; min-width: 0;">
                          <input 
                            class="input" 
                            type="text" 
                            placeholder="R$"
                            style="min-height: 2.5rem; ${this.splitMode !== 'value' ? 'background-color: var(--bg-surface-secondary, #1D2740); opacity: 0.7;' : ''}"
                            .value="${parseFloat(split.value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}"
                            ?readonly="${this.splitMode === 'percentage'}"
                            @input="${(e) => this._onSplitValueInput(e, index)}"
                          />
                        </div>
                        <button 
                          type="button" 
                          class="button is-danger is-light" 
                          style="min-height: 2.5rem; min-width: 2.5rem; flex-shrink: 0;"
                          data-cy="btn-remover-split"
                          @click="${() => this._removeSplit(index)}"
                          ?disabled="${this.splits.length <= 1}"
                        >
                          <span class="icon">
                            <i class="fas fa-trash-alt"></i>
                          </span>
                        </button>
                      </div>
                    </div>
                  `)}

                  <!-- Botão Adicionar responsável — alinhado à direita -->
                  <div class="is-flex is-justify-content-flex-end mt-2">
                    <button type="button" class="button is-primary is-small" @click="${this._addSplit}">
                      <span class="icon is-small">
                        <i class="fas fa-plus"></i>
                      </span>
                      <span>Adicionar</span>
                    </button>
                  </div>

                  ${this.splitMode === 'percentage' ? html`
                    <div class="notification ${this._getTotalPercentage() === 100 ? 'is-success' : 'is-warning'} is-light p-2 mt-2">
                      <span class="is-size-7">Total: ${this._getTotalPercentage()}% ${this._getTotalPercentage() === 100 ? '✓' : '(deve ser 100%)'}</span>
                    </div>
                  ` : html`
                    ${(() => {
                      const totalSplit = this._getTotalSplitValue()
                      const totalTransacao = this._getTotalValue()
                      const diff = parseFloat((totalSplit - totalTransacao).toFixed(2))
                      const isOk = Math.abs(diff) < 0.01
                      return html`
                        <div class="notification ${isOk ? 'is-success' : 'is-warning'} is-light p-2 mt-2">
                          <span class="is-size-7">
                            Total: ${totalSplit.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            ${isOk ? '✓' : diff > 0
                              ? `— Excede ${Math.abs(diff).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`
                              : `— Faltam ${Math.abs(diff).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`
                            }
                          </span>
                        </div>
                      `
                    })()}
                  `}
                </div>
              ` : ''}
              ` : ''}

              <!-- Parcelar (ativado pelo toggle de modalidade, oculto quando tipo = transferência) -->
              ${!this.isEditMode ? html`
                ${this.selectedType !== 'transferencia' ? html`

                ${this.isInstallment ? html`
                  <div class="box" style="padding: 1rem; border-radius: var(--radius-input, 12px); margin-bottom: 1rem; background-color: var(--bg-surface-secondary, #1D2740);">
                    <p class="is-size-7 has-text-weight-medium mb-3" style="color: var(--text-secondary, #A8B3CF);">Divide a despesa em várias parcelas</p>
                    <div class="field mb-4">
                      <label class="label is-size-7 has-text-weight-medium">Número de Parcelas</label>
                      <div class="control">
                        <input
                          class="input"
                          type="number"
                          min="2"
                          max="48"
                          placeholder="Ex: 12"
                          data-cy="input-parcelas"
                          @input="${() => this.requestUpdate()}"
                        />
                      </div>
                      <p class="help">Valor por parcela: R$ ${this._getInstallmentValue()}</p>
                    </div>
                  </div>
                ` : ''}
                ` : ''}

                <!-- Recorrente (ativado pelo toggle de modalidade) -->
                ${this.isRecurring ? html`
                  <div class="box" style="padding: 1rem; border-radius: var(--radius-input, 12px); margin-bottom: 1rem; background-color: var(--bg-surface-secondary, #1D2740);">
                    <p class="is-size-7 has-text-weight-medium mb-3" style="color: var(--text-secondary, #A8B3CF);">Despesa que se repete periodicamente</p>
                    <div class="field mb-4">
                      <label class="label is-size-7 has-text-weight-medium">Frequência</label>
                      <div class="control">
                        <div class="select is-fullwidth">
                          <select data-cy="select-frequencia">
                            <option value="daily">Diária</option>
                            <option value="weekly">Semanal</option>
                            <option value="monthly" selected>Mensal</option>
                            <option value="yearly">Anual</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div class="field mb-4">
                      <label class="checkbox">
                        <input type="checkbox" data-cy="checkbox-definir-termino" @change="${this._toggleRecurringEnd}" /> 
                        Definir término
                      </label>
                    </div>

                    ${this.hasRecurringEnd ? html`
                      <div style="padding-left: 1.5rem;">
                        <div class="field mb-4">
                          <div class="buttons has-addons">
                            <button 
                              type="button" 
                              class="button is-small ${this.recurringEndType === 'count' ? 'is-primary' : ''}"
                              @click="${() => this._setRecurringEndType('count')}"
                            >
                              Por quantidade
                            </button>
                            <button 
                              type="button" 
                              class="button is-small ${this.recurringEndType === 'date' ? 'is-primary' : ''}"
                              @click="${() => this._setRecurringEndType('date')}"
                            >
                              Por data
                            </button>
                          </div>
                        </div>

                        ${this.recurringEndType === 'count' ? html`
                          <div class="field mb-4">
                            <label class="label is-size-7 has-text-weight-medium">Número de Ocorrências</label>
                            <div class="control">
                              <input
                                class="input"
                                type="number"
                                min="1"
                                placeholder="Ex: 12"
                                data-cy="input-ocorrencias"
                              />
                            </div>
                          </div>
                        ` : html`
                          <div class="field mb-4">
                            <label class="label is-size-7 has-text-weight-medium">Data Final</label>
                            <div class="control has-icons-left">
                              <input
                                class="input"
                                type="date"
                                data-cy="input-data-final"
                              />
                              <span class="icon is-left">
                                <i class="fas fa-calendar"></i>
                              </span>
                            </div>
                          </div>
                        `}
                      </div>
                    ` : ''}
                  </div>
                ` : ''}
              ` : ''}

              <!-- Botões movidos para o footer fixo -->
            </form>

            <!-- Mensagem de erro inline posicionada abaixo dos campos e acima do footer -->
            ${this.renderErroSalvar()}
          </div>

          <!-- Zona 3: Footer fixo com botões de ação e sombra sobre o conteúdo -->
          <footer class="card-footer"
            data-cy="footer-modal-transacao"
            aria-label="Ações do modal de transação"
            style="
              flex-shrink: 0;
              padding: 1rem 1.5rem;
              background-color: var(--bg-surface, #151C30);
              border-top: 1px solid var(--border, #26304A);
              position: relative;
              z-index: 2;
              box-shadow: var(--shadow-footer-modal, 0 -4px 12px rgba(0,0,0,0.3));
            "
          >
            <div class="field is-grouped is-justify-content-flex-end" style="width: 100%">
              <div class="control">
                <button 
                  class="button" 
                  type="button" 
                  data-cy="btn-cancelar"
                  ?disabled="${this._saving}"
                  @click="${this.close}"
                  @mouseenter="${this._onCancelHover}"
                  @mouseleave="${this._onCancelLeave}"
                >
                  Cancelar
                </button>
              </div>
              <div class="control">
                <button 
                  class="button is-primary ${this._saving ? 'is-loading' : ''}" 
                  type="button" 
                  data-cy="btn-salvar"
                  ?disabled="${this._saving}"
                  @click="${this._handleSave}"
                  @mouseenter="${this._onSaveHover}"
                  @mouseleave="${this._onSaveLeave}"
                >
                  ${this.isEditMode ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </div>
          </footer>
        </div>

        <modal-categoria @category-save="${this._handleCategoriaSave}"></modal-categoria>
        <modal-conta @account-save="${this._handleContaSave}"></modal-conta>
        <modal-responsavel @responsavel-save="${this._handleResponsavelSave}" @modal-closed="${this._onResponsavelModalClosed}"></modal-responsavel>
        <modal-tag @tag-save="${this._handleTagSave}" @modal-closed="${this._onTagModalClosed}"></modal-tag>
      </div>
    `
  }
}

customElements.define('modal-transacao', ModalTransacao)